import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { seoConversions } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Loading, Empty, DataTable, num, pct } from "@/components/admin/seo/SeoUi";

export const Route = createFileRoute("/admin/seo/conversions")({
  head: () => ({ meta: [{ title: "Conversion funnel · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ConversionsPage,
});

const CHANNEL: Record<string, string> = {
  organic_search: "Search",
  ai_answer: "AI assistants",
  referral: "Other websites",
  direct: "Direct",
  paid: "Paid",
  email: "Email",
  social: "Social",
};

function ConversionsPage() {
  const load = useServerFn(seoConversions);
  const q = useQuery({ queryKey: ["seo-conversions"], queryFn: () => load({ data: { days: 28 } }) });
  const d = q.data as any;

  return (
    <SeoPage
      title="Conversion funnel"
      description="How people move from seeing you in search, to visiting, to enquiring, to applying — and which channels deserve the credit."
    >
      {q.isLoading ? (
        <Loading />
      ) : !d ? (
        <Empty>No data yet.</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Seen in search" value={d.funnel.impressions ? num(d.funnel.impressions) : "—"} />
            <Stat label="Visits from search" value={d.funnel.clicks ? num(d.funnel.clicks) : "—"} />
            <Stat label="Enquiries" value={num(d.funnel.leads)} />
            <Stat label="Applications" value={num(d.funnel.applications)} />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Stat label="Search click rate" value={d.funnel.clickThroughRate === null ? "—" : pct(d.funnel.clickThroughRate)} />
            <Stat label="Visit → enquiry" value={d.funnel.clickToLead === null ? "—" : pct(d.funnel.clickToLead)} />
            <Stat label="Enquiry → application" value={d.funnel.leadToApplication === null ? "—" : pct(d.funnel.leadToApplication)} />
          </div>
          {d.note ? <p className="mt-3 text-sm text-muted-foreground">{d.note}</p> : null}

          <Panel title="Credit by channel" subtitle="First touch, last touch and an even split across every step of the journey.">
            {d.attribution.length === 0 ? (
              <Empty>No completed journeys recorded in this period.</Empty>
            ) : (
              <DataTable
                head={["Channel", "First touch", "Last touch", "Even split"]}
                rows={d.attribution.map((a: any) => [CHANNEL[a.channel] ?? a.channel, num(a.first), num(a.last), a.linear])}
              />
            )}
          </Panel>

          <Panel title="Most visited pages">
            {d.topLandingPages.length === 0 ? (
              <Empty>No page views recorded yet.</Empty>
            ) : (
              <DataTable head={["Page", "Views"]} rows={d.topLandingPages.map((p: any) => [p.path, num(p.views)])} />
            )}
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
